import React, { useState } from "react";
import {
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Switch,
  Flex,
  Badge,
} from "@chakra-ui/react";
import {
  EditIcon,
  CheckIcon,
  CloseIcon,
  LinkIcon,
  CopyIcon,
  DeleteIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../routes/axiosInstance";
import { formatTimeStamp } from "../../../logics/timeLogic";
import { slugifyText } from "../../../logics/spaceLogic";

const OverviewTab = ({ space, currentUser, onSpaceUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: space?.title || "",
    kind: space?.kind || "",
    participantMode: space?.participantMode || "",
    isIncognito: space?.isIncognito || false,
    metaData: space?.metaData || {},
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      };
      const response = await axiosInstance.put(
        `/api/spaces/${space._id}`,
        form,
        config,
      );
      toast({
        title: "Space updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditMode(false);
      if (onSpaceUpdate) {
        onSpaceUpdate(response.data.data);
      }
    } catch (err) {
      toast({
        title: "Update failed",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (isActive) => {
    try {
      setStatusUpdating(true);
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      };
      const response = await axiosInstance.patch(
        `/api/spaces/${space._id}/status`,
        { isActive },
        config,
      );
      toast({
        title: `Space ${isActive ? "activated" : "deactivated"} successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (onSpaceUpdate) {
        onSpaceUpdate(response.data.data);
      }
    } catch (err) {
      toast({
        title: "Status update failed",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      };
      await axiosInstance.delete(`/api/spaces/${space._id}`, config);
      toast({
        title: "Space deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setDeleteOpen(false);
      navigate("/spaces");
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const shareUrl = `${window.location.origin}/s/${
    space?.referenceId
  }/${slugifyText(space?.title)}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({
      title: "Link copied to clipboard!",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <VStack align="stretch" spacing={4}>
      {/* Header with action buttons */}
      <Card p={4} borderRadius="xl" boxShadow="md">
        <HStack justifyContent="space-between" mb={3}>
          <Text fontSize="lg" fontWeight="bold" color="teal.600">
            Space Overview
          </Text>
          <HStack spacing={2}>
            {!editMode && space?.referenceId && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="teal"
                onClick={() => setShareOpen(true)}
                _hover={{ bg: "teal.50" }}
                aria-label="Share space"
              >
                <LinkIcon />
              </Button>
            )}
            {!editMode && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => setDeleteOpen(true)}
                _hover={{ bg: "red.50" }}
                aria-label="Delete space"
              >
                <DeleteIcon />
              </Button>
            )}
            {!editMode ? (
              <Button
                size="sm"
                colorScheme="teal"
                variant="solid"
                onClick={() => setEditMode(true)}
                borderRadius="md"
                aria-label="Edit space"
              >
                <EditIcon />
              </Button>
            ) : (
              <HStack spacing={2}>
                <Button
                  leftIcon={<CheckIcon />}
                  size="sm"
                  colorScheme="green"
                  variant="solid"
                  onClick={handleUpdate}
                  isLoading={loading}
                  borderRadius="md"
                >
                  Save
                </Button>
                <Button
                  leftIcon={<CloseIcon />}
                  size="sm"
                  variant="outline"
                  onClick={() => setEditMode(false)}
                  borderRadius="md"
                >
                  Cancel
                </Button>
              </HStack>
            )}
          </HStack>
        </HStack>

        {/* Space Status Toggle */}
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          p={3}
          bg="gray.50"
          borderRadius="md"
        >
          <HStack spacing={3}>
            <Badge
              colorScheme={space?.isActive ? "green" : "red"}
              variant="solid"
              borderRadius="full"
              px={3}
              py={1}
            >
              {space?.isActive ? "Active" : "Inactive"}
            </Badge>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              {space?.isActive ? "Deactivate" : "Activate"}
            </Text>
            <Switch
              isChecked={space?.isActive || false}
              onChange={(e) => handleStatusToggle(e.target.checked)}
              colorScheme="teal"
              size="md"
              isDisabled={statusUpdating}
            />
          </HStack>
        </Flex>

        {/* Space details */}
        {!editMode ? (
          <VStack align="stretch" spacing={3}>
            <DetailItem label="Title" value={space?.title} />
            {space?.description && (
              <DetailItem label="Description" value={space.description} />
            )}
            <DetailItem
              label="Participant Mode"
              value={space?.participantMode}
            />
            <DetailItem
              label="Incognito Mode"
              value={space?.isIncognito ? "Enabled" : "Disabled"}
            />
            {space?.isIncognito && space?.owner?.alias && (
              <DetailItem label="Choosen Alias" value={space.owner.alias} />
            )}
            <DetailItem
              label="Created"
              value={formatTimeStamp(space.createdAt)}
            />
            {space?.expiresAt && (
              <DetailItem
                label="Expires"
                value={formatTimeStamp(space.expiresAt)}
              />
            )}
            {space?.referenceId && (
              <DetailItem label="Reference ID" value={space.referenceId} />
            )}
          </VStack>
        ) : (
          <VStack as="form" align="stretch" spacing={3}>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm">
                Title
              </FormLabel>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                borderRadius="md"
                focusBorderColor="teal.400"
                size="sm"
              />
            </FormControl>

            {/* Metadata fields based on kind */}
            {renderMetadataFields(form, setForm)}
          </VStack>
        )}
      </Card>

      {/* Share Modal */}
      <Modal isOpen={shareOpen} onClose={() => setShareOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            <HStack color="teal.600">
              <LinkIcon />
              <Text>Share Space</Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <Text mb={4} color="gray.600">
              Share this link with others to invite them to your space:
            </Text>
            <HStack>
              <Input
                value={shareUrl}
                isReadOnly
                bg="gray.50"
                borderRadius="md"
              />
              <Button
                leftIcon={<CopyIcon />}
                colorScheme={copied ? "green" : "teal"}
                onClick={copyToClipboard}
                borderRadius="md"
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setShareOpen(false)} borderRadius="md">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            <HStack color="red.500">
              <WarningIcon />
              <Text>Delete Space</Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <Text color="red.600">
              Are you sure you want to delete "{space?.title}"? This action
              cannot be undone and all members will be removed.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => setDeleteOpen(false)}
              variant="outline"
              mr={3}
              borderRadius="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={deleting}
              onClick={handleDelete}
              borderRadius="md"
            >
              Delete Space
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

// Helper component for detail items
const DetailItem = ({ label, value }) => (
  <HStack justify="space-between" p={2} bg="gray.50" borderRadius="md">
    <Text fontWeight="medium" color="gray.600" fontSize="sm">
      {label}:
    </Text>
    <Text color="gray.800" fontSize="sm" fontWeight="semibold">
      {value}
    </Text>
  </HStack>
);

// Helper function to render metadata fields
const renderMetadataFields = (form, setForm) => {
  switch (form.kind) {
    case "CHAT":
      return (
        <FormControl>
          <FormLabel fontWeight="semibold">Allowed Members</FormLabel>
          <Input
            type="number"
            value={form.metaData?.allowedMembers || ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                metaData: {
                  ...f.metaData,
                  allowedMembers: e.target.value,
                },
              }))
            }
            borderRadius="md"
            focusBorderColor="teal.400"
          />
        </FormControl>
      );

    case "QNA":
      return (
        <FormControl>
          <FormLabel fontWeight="semibold">Question</FormLabel>
          <Input
            value={form.metaData?.question || ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                metaData: {
                  ...f.metaData,
                  question: e.target.value,
                },
              }))
            }
            borderRadius="md"
            focusBorderColor="teal.400"
          />
        </FormControl>
      );

    case "POLL":
      return (
        <>
          <FormControl>
            <FormLabel fontWeight="semibold">Poll Question</FormLabel>
            <Input
              value={form.metaData?.question || ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  metaData: {
                    ...f.metaData,
                    question: e.target.value,
                  },
                }))
              }
              borderRadius="md"
              focusBorderColor="teal.400"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontWeight="semibold">Options</FormLabel>
            <VStack align="stretch" spacing={2}>
              {(form.metaData?.options || []).map((opt, idx) => (
                <HStack key={opt.id || idx}>
                  <Input
                    value={opt.label || ""}
                    onChange={(e) => {
                      const newOpts = [...form.metaData.options];
                      newOpts[idx] = {
                        ...newOpts[idx],
                        label: e.target.value,
                      };
                      setForm((f) => ({
                        ...f,
                        metaData: {
                          ...f.metaData,
                          options: newOpts,
                        },
                      }));
                    }}
                    borderRadius="md"
                    focusBorderColor="teal.400"
                  />
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => {
                      const newOpts = form.metaData.options.filter(
                        (_, i) => i !== idx,
                      );
                      setForm((f) => ({
                        ...f,
                        metaData: {
                          ...f.metaData,
                          options: newOpts,
                        },
                      }));
                    }}
                    borderRadius="md"
                  >
                    Remove
                  </Button>
                </HStack>
              ))}
              <Button
                size="sm"
                colorScheme="teal"
                variant="outline"
                onClick={() => {
                  setForm((f) => ({
                    ...f,
                    metaData: {
                      ...f.metaData,
                      options: [
                        ...(f.metaData?.options || []),
                        { id: Date.now(), label: "" },
                      ],
                    },
                  }));
                }}
                borderRadius="md"
              >
                Add Option
              </Button>
            </VStack>
          </FormControl>
        </>
      );

    case "FORM":
      return (
        <FormControl>
          <FormLabel fontWeight="semibold">Form Schema (JSON)</FormLabel>
          <Input
            value={JSON.stringify(form.metaData?.formSchema || [], null, 2)}
            onChange={(e) => {
              let val = [];
              try {
                val = JSON.parse(e.target.value);
              } catch {}
              setForm((f) => ({
                ...f,
                metaData: { ...f.metaData, formSchema: val },
              }));
            }}
            borderRadius="md"
            focusBorderColor="teal.400"
          />
        </FormControl>
      );

    case "BROADCAST":
      return (
        <FormControl>
          <FormLabel fontWeight="semibold">Announcement</FormLabel>
          <Input
            value={form.metaData?.announcement || ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                metaData: {
                  ...f.metaData,
                  announcement: e.target.value,
                },
              }))
            }
            borderRadius="md"
            focusBorderColor="teal.400"
          />
        </FormControl>
      );

    default:
      return null;
  }
};

export default OverviewTab;
